import { useEffect, useRef, useState } from "react";
import {
  CHECKIN_CLOSE_REASONS,
  closeReasonLabel,
  type CheckinCoachHandlingDetail,
  type CoachPatientDetail,
} from "@care-by-ella/contracts";
import { AiAssistCoachReply } from "@/components/AiAssistCoachReply";
import {
  CloseReasonPicker,
  EMPTY_CLOSE_REASON,
  closeReasonBody,
  closeReasonReady,
  type CloseReasonValue,
} from "@/components/CloseReasonPicker";
import { WeeklyCheckinAnswers } from "@/components/WeeklyCheckinAnswers";
import { flagRuleLabel } from "@/lib/flag-rule-labels";
import {
  checkinRowTitle,
  checkinStatus,
  checkinStatusLabel,
  shortDate,
  splitCheckins,
} from "@/lib/checkin-list";
import {
  checkinActionErrorText,
  closeCheckinWithoutReply,
  fetchCheckinHandling,
  sendCheckinCoachReply,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { hiddenWhenReadOnly, useReadOnly } from "./SupportView";
import { checkinAnchor, useHashTarget } from "@/lib/checkin-anchor";

// TES-690 — the coach's half of the Incheckningar lane.
//
// The lane has always routed the non-escalating flagged check-ins to her, and
// the API has always let her stamp `reviewedAt`. What was missing was anywhere
// to do it: "Markera som granskad" existed only on the doctor's patient page,
// which a coach can never mount, so her queue and its badge grew without a
// bottom and only a doctor could clear them — the exact escalation the routing
// was built to avoid.
//
// Ö-58 settles the scope: she sees "relevanta
// incheckningar". The server draws that line, so there is no filtering here
// to get wrong. TES-986 (Ö-266) — a check-in whose medical answers went to
// the doctor arrives as a status row: no answers, no flags, only
// `medicalStatus` (MEDICAL_REVIEW_NEUTRAL_STATUS, A-01's sentence, shown as
// the row's status; TES-1213). It gets nothing
// to review and no AI draft; the doctor owns it.
//
// TES-899 — "Markera som granskad" is gone. An open check-in is finished in
// one of two ways: "Skicka svar och slutför" (in the reply box below it) or
// "Avsluta utan svar" with a mandatory reason. Both are the check-in's own
// actions, so a check-in answered here, in the work queue or in another tab
// shows as answered everywhere and cannot be answered twice. A check-in
// stamped "granskad" before this existed is shown as reviewed and left alone.
//
// TES-899 parts 3–4 — the list is split in two, as Emelie asked:
// "Behöver hanteras" (open, expanded, with the actions) and
// "Tidigare incheckningar" (answered, closed, reviewed or the doctor's;
// collapsed rows that open).
// Every row says its week, full submission date and status. An opened
// answered row shows the reply it was answered with, read from the coach
// thread; a closed one shows why no reply was needed.

type Checkin = CoachPatientDetail["weeklyCheckins"][number];

function Flags({ flags }: { flags: Checkin["flags"] }) {
  if (flags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-xs mb-xs">
      {flags.map((f) => (
        <span
          key={f.rule}
          className="rounded-pill border border-warning/40 bg-warning/10 px-sm py-[2px] text-xs text-warning"
          title={f.rule}
        >
          {flagRuleLabel(f.rule)}
        </span>
      ))}
    </div>
  );
}

export function CoachWeeklyCheckins({
  patientId,
  checkins,
  onReviewed,
  onUnauthorized,
}: {
  patientId: string;
  checkins: Checkin[];
  onReviewed: () => void;
  onUnauthorized: () => void;
}) {
  // TES-1040 — no "Avsluta utan svar" in the admin's read-only view.
  const readOnly = useReadOnly();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [aiAvailable, setAiAvailable] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // "Avsluta utan svar" asks for its reason before it does anything.
  const [closingId, setClosingId] = useState<string | null>(null);
  const [reason, setReason] = useState<CloseReasonValue>(EMPTY_CLOSE_REASON);

  async function closeWithoutReply(submissionId: string) {
    if (!closeReasonReady(reason)) return;
    setBusyId(submissionId);
    setError(null);
    try {
      await closeCheckinWithoutReply(
        patientId,
        submissionId,
        closeReasonBody(reason),
      );
      setClosingId(null);
      setReason(EMPTY_CLOSE_REASON);
      onReviewed();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(checkinActionErrorText(e));
    } finally {
      setBusyId(null);
    }
  }

  const { needsHandling, earlier } = splitCheckins(checkins);

  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md">
      <h2 className="font-display text-lg mb-xs">Incheckningar</h2>
      <p className="text-xs text-textSecondary mb-md">
        De incheckningar som ligger hos dig. Medicinska svar hanteras av läkare
        och visas inte här.
      </p>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm mb-sm">
          {error}
        </div>
      ) : null}

      <h3 className="text-sm font-semibold mb-xs">Behöver hanteras</h3>
      {needsHandling.length === 0 ? (
        <p className="text-textSecondary text-sm mb-md">
          Inget att hantera just nu.
        </p>
      ) : (
        <ul className="flex flex-col gap-sm mb-md">
          {needsHandling.map((c) => {
            const flagged = c.flags.length > 0;
            return (
              <li
                key={c.id}
                id={checkinAnchor(c.id)}
                className={`scroll-mt-48 rounded-sm border p-sm ${flagged ? "border-warning/60 bg-warning/5" : "border-surfaceMuted"}`}
              >
                <div className="flex flex-wrap items-center gap-sm mb-xs">
                  {/* TES-1210 — A-01: identity, date and status must not
                      rest on small secondary text alone. */}
                  <p className="text-sm text-textPrimary">
                    {checkinRowTitle(c)}
                    <span className="text-xs text-textSecondary">
                      {" "}
                      · språk {c.submittedInLocale.toUpperCase()}
                    </span>
                  </p>
                  <span className="rounded-pill border border-warning/50 px-sm py-[2px] text-xs text-warning">
                    {checkinStatusLabel(checkinStatus(c))}
                  </span>
                  {closingId !== c.id && !readOnly ? (
                    <button
                      type="button"
                      disabled={busyId === c.id}
                      onClick={() => {
                        setClosingId(c.id);
                        setReason(EMPTY_CLOSE_REASON);
                      }}
                      className="ml-auto rounded-md border border-surfaceMuted px-md py-xs text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50"
                    >
                      Avsluta utan svar
                    </button>
                  ) : null}
                </div>
                <Flags flags={c.flags} />
                {closingId === c.id ? (
                  <div className="mb-xs flex flex-col gap-xs">
                    <CloseReasonPicker
                      name={`close-reason-${c.id}`}
                      legend="Varför behövs inget svar?"
                      options={CHECKIN_CLOSE_REASONS}
                      value={reason}
                      onChange={setReason}
                      disabled={busyId === c.id}
                    />
                    <div className="flex flex-wrap gap-sm">
                      <button
                        type="button"
                        disabled={busyId === c.id || !closeReasonReady(reason)}
                        onClick={() => void closeWithoutReply(c.id)}
                        className="rounded-md bg-brand px-md py-xs text-sm text-textOnBrand hover:opacity-90 disabled:opacity-50"
                      >
                        {busyId === c.id ? "Sparar…" : "Bekräfta avslut"}
                      </button>
                      <button
                        type="button"
                        disabled={busyId === c.id}
                        onClick={() => setClosingId(null)}
                        className="rounded-md border border-surfaceMuted px-md py-xs text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50"
                      >
                        Avbryt
                      </button>
                    </div>
                  </div>
                ) : null}
                <MedicalNote checkin={c} />
                <WeeklyCheckinAnswers
                  answers={c.answers}
                  templateKey={c.templateKey}
                />
                {/* TES-710 — the reply draft below the check-in. The first
                    404 (AI assist off on the box) turns it off for all
                    rows. TES-1561 — a monthly check-in has no draft; it and
                    every row without one get a plain reply box, so
                    "Skicka svar och slutför" is always here. */}
                {aiAvailable && c.templateKey !== "milestone" ? (
                  <AiAssistCoachReply
                    patientId={patientId}
                    submissionId={c.id}
                    onUnauthorized={onUnauthorized}
                    onDisabled={() => setAiAvailable(false)}
                    onHandled={onReviewed}
                  />
                ) : (
                  <PlainCoachReply
                    patientId={patientId}
                    submissionId={c.id}
                    onUnauthorized={onUnauthorized}
                    onHandled={onReviewed}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      <h3 className="text-sm font-semibold mb-xs">Tidigare incheckningar</h3>
      {earlier.length === 0 ? (
        <p className="text-textSecondary text-sm">
          Inga tidigare incheckningar.
        </p>
      ) : (
        <ul className="flex flex-col gap-xs">
          {earlier.map((c) => (
            <EarlierCheckin
              key={c.id}
              patientId={patientId}
              checkin={c}
              onUnauthorized={onUnauthorized}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

// A handled check-in: one collapsed line until she opens it. The reply or
// reason is fetched on the first open, not for every row on page load.
function EarlierCheckin({
  patientId,
  checkin: c,
  onUnauthorized,
}: {
  patientId: string;
  checkin: Checkin;
  onUnauthorized: () => void;
}) {
  const [opened, setOpened] = useState(false);
  const status = checkinStatus(c);
  // TES-1074 — a trend point links here; the row opens when it is the target.
  const anchor = checkinAnchor(c.id);
  const targeted = useHashTarget(anchor);
  return (
    <li
      id={anchor}
      className="scroll-mt-48 rounded-sm border border-surfaceMuted"
    >
      <details
        open={targeted || undefined}
        onToggle={(e) => e.currentTarget.open && setOpened(true)}
      >
        <summary className="cursor-pointer list-inside p-sm text-sm">
          <span className="text-textPrimary">{checkinRowTitle(c)}</span>
          <span
            className={`ml-sm text-xs ${status.kind === "medical" ? "text-textSecondary" : "text-success"}`}
          >
            {checkinStatusLabel(status)}
          </span>
        </summary>
        <div className="px-sm pb-sm">
          {/* A routed row's status line already IS the neutral sentence;
              repeating it in the body would say it twice. TES-1561 — a
              routed row that is also her case shows her part. */}
          {status.kind === "medical" ? null : (
            <>
              {opened &&
              (status.kind === "answered" ||
                status.kind === "closed_without_reply") ? (
                <HandlingHistory
                  patientId={patientId}
                  submissionId={c.id}
                  onUnauthorized={onUnauthorized}
                />
              ) : null}
              <Flags flags={c.flags} />
              <MedicalNote checkin={c} />
              <WeeklyCheckinAnswers
                answers={c.answers}
                templateKey={c.templateKey}
              />
            </>
          )}
        </div>
      </details>
    </li>
  );
}

// TES-1561 (Ö-866) — a check-in that is her case AND went to the doctor:
// A-01's neutral sentence above her part, so she knows the medical answers
// are with the doctor and are not hers to comment on.
function MedicalNote({ checkin }: { checkin: Checkin }) {
  if (!checkin.medicalStatus || checkin.coachCase !== true) return null;
  return (
    <p className="mb-xs text-xs text-textSecondary">{checkin.medicalStatus}</p>
  );
}

// TES-1561 — "Skicka svar och slutför" without an AI draft: the same action
// the queue card's reply box calls, so the check-in completes everywhere.
function PlainCoachReplyPanel({
  patientId,
  submissionId,
  onUnauthorized,
  onHandled,
}: {
  patientId: string;
  submissionId: string;
  onUnauthorized: () => void;
  onHandled: () => void;
}) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await sendCheckinCoachReply(patientId, submissionId, { body });
      onHandled();
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else setError(checkinActionErrorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-sm rounded-md border border-surfaceMuted bg-surfaceMuted/40 p-sm">
      {error ? <p className="mb-xs text-sm text-danger">{error}</p> : null}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        className="w-full rounded-md border border-surfaceMuted bg-surface p-sm text-sm text-textPrimary placeholder:text-textDisabled focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
        aria-label="Svar"
        placeholder="Skriv ditt svar."
      />
      <button
        type="button"
        disabled={busy || text.trim() === ""}
        onClick={() => void send()}
        className="mt-xs rounded-md bg-brand px-md py-xs text-sm font-semibold text-white disabled:opacity-50"
      >
        {busy ? "Skickar…" : "Skicka svar och slutför"}
      </button>
    </div>
  );
}

// TES-1040 — no reply in the admin's read-only support view.
const PlainCoachReply = hiddenWhenReadOnly(PlainCoachReplyPanel);

function HandlingHistory({
  patientId,
  submissionId,
  onUnauthorized,
}: {
  patientId: string;
  submissionId: string;
  onUnauthorized: () => void;
}) {
  const [detail, setDetail] = useState<CheckinCoachHandlingDetail | null>(null);
  const [failed, setFailed] = useState(false);
  // The parent passes a fresh callback each render; reading it through a ref
  // keeps a re-render from fetching again.
  const unauthorized = useRef(onUnauthorized);
  unauthorized.current = onUnauthorized;

  useEffect(() => {
    let live = true;
    fetchCheckinHandling(patientId, submissionId)
      .then((d) => live && setDetail(d))
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) unauthorized.current();
        else setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [patientId, submissionId]);

  const box = "mb-sm rounded-sm bg-surfaceMuted/40 p-sm text-sm";
  if (failed) {
    return (
      <p className="mb-sm text-xs text-danger">
        Det gick inte att hämta svaret. Öppna raden igen för att försöka på
        nytt.
      </p>
    );
  }
  if (!detail) {
    return <p className="mb-sm text-xs text-textSecondary">Hämtar…</p>;
  }
  if (detail.state === "answered") {
    return (
      <div className={box}>
        <p className="text-xs text-textSecondary mb-xs">
          Svar i coachchatten
          {detail.reply
            ? ` · ${shortDate(detail.reply.createdAt)}${detail.reply.editedAt ? " · redigerat" : ""}`
            : ""}
        </p>
        {detail.reply ? (
          <p className="whitespace-pre-wrap">{detail.reply.body}</p>
        ) : (
          <p className="text-textSecondary">
            Meddelandet finns inte längre i chatten.
          </p>
        )}
      </div>
    );
  }
  if (detail.state === "closed_without_reply") {
    return (
      <div className={box}>
        {/* TES-1208 — the structured reason, then the coach's note. A
            check-in closed before the codes existed reads "Annat" with its
            original text as the note. */}
        <p className="text-xs text-textSecondary mb-xs">
          Avslutad utan svar · orsak:{" "}
          {detail.reasonCode
            ? closeReasonLabel(detail.reasonCode, "checkin")
            : "Annat"}
        </p>
        {detail.reason ? (
          <p className="whitespace-pre-wrap">{detail.reason}</p>
        ) : null}
      </div>
    );
  }
  return null;
}
